import { joiResolver } from '@hookform/resolvers/joi';
import dayjs, { Dayjs } from 'dayjs';

import { makeStyles } from 'tss-react/mui';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ApiButton,
  Box,
  ContentTemplate,
  CustomSelect,
  DropDownItem,
  ErrorInfo,
  Grid,
  Typography,
} from 'web-library';
import { JobReportFilters } from '../../../../models/JobName';
import jobStatusReportSchema from '../../schema/jobStatusReportSchema';
import ControlledDatePicker from "web-library/stories/atom/DatePicker/ControlledDatePicker";

const useStyles = makeStyles()((theme) =>
({
  jobNameContainer: {
    marginBottom: theme.spacing(4.5),
  },
  dateContainers: {
    marginBottom: theme.spacing(2),
  },
  startDateContainer: {
    marginRight: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      marginRight: 0,
      marginBottom: theme.spacing(4.5),
    },
  },
  endDateContainer: {},
  dateRangeContainer: {
    marginBottom: theme.spacing(4),
  },
  dateRangeCaptionText: {
    color: theme.palette.text.secondary,
  },
  dateRangeHelperText: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

interface JobStatusReportSearchFormProps {
  jobNameOptions: DropDownItem[];
  onConfirmFilters: (jobPayload: JobReportFilters) => void;
  initialFilters?: JobReportFilters;
}

const JobStatusReportSearchForm = ({
  jobNameOptions,
  onConfirmFilters,
  initialFilters,
}: JobStatusReportSearchFormProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const [startDateState, setStartDate] = useState(initialFilters?.dates.startDate || null);
  const [endDateState, setEndDate] = useState(initialFilters?.dates.endDate || null);

  const jobReportStatusForm = useForm<JobReportFilters>({
    mode: 'onSubmit',
    resolver: joiResolver(jobStatusReportSchema),
    defaultValues: initialFilters,
  });
  const {
    setValue,
    formState: { errors, isDirty },
    handleSubmit,
    control,
    reset,
  } = jobReportStatusForm;

  const onSubmit = useCallback(
    (data: JobReportFilters) => {
      if (!isDirty) {
        return;
      }

      const {
        jobKey,
        dates: { startDate, endDate },
      } = data;

      if (dayjs(endDate).isBefore(dayjs(startDate))) {
        jobReportStatusForm.setError('dates.endDate', {
          type: 'custom',
          message: 'End Date should be greater than Start Date',
        });
        return;
      }

      const formattedStartDate = startDate;
      const formattedEndDate = endDate;
      onConfirmFilters({
        jobKey,
        dates: {
          startDate: formattedStartDate,
          endDate: formattedEndDate,
        },
      });

      reset({
        jobKey,
        dates: {
          startDate: formattedStartDate,
          endDate: formattedEndDate,
        },
      });
    },
    [isDirty, onConfirmFilters, reset]
  );

  return (
    <ContentTemplate transparent>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container className={classes.jobNameContainer}>
          <Grid xs={12} sm={5}>
            <CustomSelect
              key={`job-name-select-${initialFilters?.jobKey}`}
              label={t('admin->>JobStatusReport.JobNameLabel')}
              labelId="job-status-report-select"
              menuItems={jobNameOptions}
              onChange={(e) => {
                setValue('jobKey', e.target.value?.toString() || '', { shouldDirty: true, shouldValidate: true });
              }}
              placeholder={t('admin->>JobStatusReport.JobNamePlaceholder')}
              isRequired
              error={!!errors.jobKey?.message}
              helperText={errors.jobKey?.message?.toString()}
              displayEmpty
              defaultValue={initialFilters?.jobKey}
            />
          </Grid>
        </Grid>
        <Grid container className={classes.dateContainers}>
          <Grid xs={12} sm={4} className={classes.startDateContainer}>
            <ControlledDatePicker
              label={t('admin->>JobStatusReport.StartDate')}
              onChange={(date: any) => {
                setStartDate(date || '');
                setValue('dates.startDate', date || '', {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }}
              name="dates.startDate"
              disableFuture
              placeholder={t('admin->>JobStatusReport.StartDatePlaceholder')}
              inputVariant="filled"
              autoOk
              isRequired
              maxDate={dayjs()}
              error={!!errors?.dates?.startDate?.message}
              helperText={errors?.dates?.startDate?.message?.toString()}
              control={control}
            />
          </Grid>
          <Grid xs={12} sm={4} className={classes.endDateContainer}>
            <ControlledDatePicker
              label={t('admin->>JobStatusReport.EndDate')}
              onChange={(date: any) => {
                setEndDate(date || '');
                setValue('dates.endDate', date || '', {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }}
              name="dates.endDate"
              disableFuture
              placeholder={t('admin->>JobStatusReport.EndDatePlaceholder')}
              inputVariant="filled"
              autoOk
              isRequired
              maxDate={dayjs()}
              error={!!errors?.dates?.endDate?.message}
              helperText={errors?.dates?.endDate?.message?.toString()}
              control={control}
            />
          </Grid>
        </Grid>
        <Box className={classes.dateRangeContainer}>
          <Typography variant="subtitle2" className={classes.dateRangeCaptionText}>
            {t('admin->>JobStatusReport.DateRangeCaption')}
          </Typography>
          {(errors?.dates as any)?.message && <ErrorInfo label={(errors?.dates as any)?.message} />}
        </Box>
        <ApiButton type="submit" status="idle" size="small">
          {t('admin->>JobStatusReport.ConfirmButtonLabel')}
        </ApiButton>
      </form>
    </ContentTemplate>
  );
};

export default JobStatusReportSearchForm;
